import { useEffect, useRef, useState } from "react";
import { Skeleton } from "@mui/material";
import { site } from "../../../_foundation/constants/site";

interface Props {
  src: string;
  alt?: string;
  className?: string;
  width?: string | number;
  height?: string | number;
  skeletonLoaderHeight?: string | number;
  transparent?: boolean;
  styles?: React.CSSProperties;
  priority?: boolean;
}

const ImageLayout: React.FC<Props> = ({
  src,
  alt,
  className,
  width,
  height,
  skeletonLoaderHeight,
  transparent,
  styles = {},
  priority = false,
}) => {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [imgSrc, setImgSrc] = useState<string>("");
  const [isIntersecting, setIsIntersecting] = useState<boolean>(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const imgDefault = site?.defaultImage;
  const observerRef = useRef<IntersectionObserver | null>(null);
  const preloadedImages = useRef<Set<string>>(new Set());

  const actualSrc = src || imgDefault;

  // Intersection observer for lazy loading
  useEffect(() => {
    if (!priority) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          setIsIntersecting(entry.isIntersecting);
        },
        {
          rootMargin: "50px",
          threshold: 0,
        }
      );

      if (imgRef.current) {
        observerRef.current.observe(imgRef.current);
      }

      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
      };
    } else {
      setIsIntersecting(true);
    }
  }, [priority]);

  // Load image only if intersecting or priority
  useEffect(() => {
    let isMounted = true;

    if (isIntersecting || priority) {
      setLoaded(false);

      if (preloadedImages.current.has(actualSrc)) {
        setImgSrc(actualSrc);
        setLoaded(true);
        return;
      }

      const img = new Image();

      const handleLoad = () => {
        if (isMounted) {
          preloadedImages.current.add(actualSrc);
          setImgSrc(actualSrc);
          setLoaded(true);
        }
      };

      const handleError = () => {
        if (isMounted) {
          const fallbackSrc = window["__isPrerender__"] ? actualSrc : imgDefault;
          preloadedImages.current.add(fallbackSrc);
          setImgSrc(fallbackSrc);
          setLoaded(true);
        }
      };

      if (img.complete) {
        handleLoad();
      } else {
        img.addEventListener("load", handleLoad);
        img.addEventListener("error", handleError);
        img.src = actualSrc;
      }

      return () => {
        isMounted = false;
        img.removeEventListener("load", handleLoad);
        img.removeEventListener("error", handleError);
      };
    }
  }, [actualSrc, imgDefault, isIntersecting, priority]);

  // Fallback on runtime image load error
  const handleImgError = () => {
    setImgSrc(imgDefault);
  };

  return (
    <>
      {!loaded || !imgSrc ? (
        <Skeleton
          ref={imgRef}
          variant="rectangular"
          animation="wave"
          width={width}
          height={skeletonLoaderHeight || height}
          sx={{ margin: "0 auto" }}
        >
          {width === "100%" && <div style={{ paddingTop: "100%" }} />}
        </Skeleton>
      ) : (
        <img
          ref={imgRef}
          className={className}
          src={imgSrc}
          alt={alt}
          width={width}
          height={height}
          loading={priority ? "eager" : "lazy"}
          decoding={priority ? "sync" : "async"}
          style={{
            margin: "0 auto",
            display: "block",
            opacity: transparent ? 0 : 1,
            ...styles,
          }}
          onError={handleImgError}
        />
      )}
    </>
  );
};

export { ImageLayout };
